<template>
  <div
    class="flowbuilder-node"
    :data-node-id="node._uuid"
  >
    <component
      :is="linkComponent"
      v-bind="linkProps"
      class="block transition-transform duration-200"
    >
      <div
        ref="el"
        class="relative min-w-[250px] rounded-sm px-6 py-5 shadow-sm ring-1 transition-[color,background-color,border-color,box-shadow] duration-150 dark:text-white/80"
        :class="[
          classes,
          dropMaxSize ? '' : 'max-w-[400px]',
          {
            'cursor-pointer hover:shadow-md hover:ring-2': isClickable,
            'cursor-move': canDrag
          },
          miniMetrics ? 'pb-7' : ''
        ]"
        :style="heatMapStyle"
        :draggable="canDrag"
        @dragstart="handleDragStart"
        @dragend="handleDragEnd"
        @click="handleNodeClick"
        @mouseenter="showTooltipForNode"
        @mouseleave="hideTooltip"
      >
        <!-- Settings menu -->
        <SettingsMenu
          :items="menuItems"
          :hide-menu="hideMenu"
          :hide-duplicate="hideDuplicate"
          :settings-icon="settingsIcon"
          :trash-icon="trashIcon"
          :duplicate-icon="duplicateIcon"
          :delete-label="deleteLabel"
          @delete-node="emit('delete-node')"
          @clone-node="handleCloneNode"
          @menu-opened="isMenuOpen = true"
          @menu-closed="handleMenuClosed"
        />

        <!-- Settings menu slot for webapp-specific menu overrides -->
        <slot name="settings-menu" />

        <!-- Context -->
        <div
          v-if="context"
          class="absolute left-0 -mt-12 flex w-full justify-center text-xs"
        >
          <div
            class="dark:text-brand-headline rounded bg-slate-900 px-1.5 py-0.5 uppercase text-white dark:bg-slate-400"
          >
            {{ context }}
          </div>
        </div>

        <!-- Needs setup indicator -->
        <div
          v-if="computedNeedsSetup && !templatePreview"
          class="absolute left-0 -mt-12 flex w-full justify-center text-xs"
        >
          <div
            class="dark:text-brand-headline rounded bg-slate-900 px-1.5 py-0.5 uppercase text-white dark:bg-slate-400"
          >
            {{ setupLabel || 'Needs Setup' }}
          </div>
        </div>

        <!-- Unreachable indicator -->
        <div
          class="absolute left-0 -mt-12 hidden w-full justify-center text-xs [.flowbuilder-unreachable_&]:flex"
        >
          <div class="rounded bg-slate-900 px-1.5 py-0.5 uppercase text-white">
            Unreachable
          </div>
        </div>

        <div class="space-y-1.5">
          <div
            class="flex items-center gap-x-1.5 text-xs font-bold text-slate-900 dark:text-white/90"
          >
            <component
              :is="resolvedIcon"
              v-if="resolvedIcon"
              class="h-5 w-5 flex-shrink-0"
            />
            <slot name="context" />
          </div>
          <div>
            <slot />
          </div>

          <div
            v-if="miniMetrics && visitorCount > 0 && useEnhancedReporting"
            class="absolute bottom-2 right-4 flex items-center justify-end gap-x-1 text-xs opacity-75"
          >
            <UsersIcon class="h-3 w-3" />
            {{ visitorCount }}
          </div>

          <!-- Hover tooltip -->
          <div v-if="$slots.hover">
            <Teleport :to="tooltipTeleportTarget">
              <div
                v-if="showTooltip"
                class="flowbuilder-tooltip"
                ref="tooltipEl"
                :style="{
                  left: tooltipX + 'px',
                  top: tooltipY + 'px',
                  position: 'fixed',
                  zIndex: 50,
                  minWidth: '300px',
                  maxHeight: tooltipMaxHeight,
                  overflowY: 'auto',
                  borderRadius: '0.25rem',
                  backgroundColor: isDark ? '#0f172a' : '#ffffff',
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#4a5568',
                  padding: 0,
                  boxShadow:
                    isDark ? 'none' : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)'
                }"
              >
                <slot name="hover" />
              </div>
            </Teleport>
          </div>
        </div>
      </div>

      <!-- Metrics display -->
      <div
        v-if="metrics && metrics.length > 0 && !miniMetrics && !preview && !templatePreview"
        class="flex justify-between space-x-3 bg-slate-300/70 px-6 py-2 ring-1 ring-slate-700/20 dark:bg-slate-900/90 dark:text-white/80 dark:ring-slate-700/50"
      >
        <div
          v-for="(metric, idx) in metrics"
          :key="idx"
        >
          <div class="text-xs font-bold">
            {{ metric.label }}
          </div>
          <div class="text-sm">
            {{ metric.value }}
          </div>
        </div>
      </div>
    </component>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, ref, unref } from 'vue'
import type { Component, Ref } from 'vue'
import { isNodeSetUp, deepCloneNode } from '../../services'
import SettingsMenu from '../ui/SettingsMenu.vue'
import { EllipsisVerticalIcon, TrashIcon, DocumentDuplicateIcon, resolveFlowIcon } from '../icons'

/**
 * BaseNode Component
 *
 * The foundation component that all flow node types extend. Provides:
 * - Common visual styling (rounded corners, shadows, hover states)
 * - Setup state indicators (shows "Needs Setup" badge)
 * - Unreachable state visualization (shows "Unreachable" badge)
 * - Metrics display area for showing node performance data
 * - Hover tooltip functionality with smart positioning
 * - Slot-based architecture for maximum flexibility
 *
 * Slots:
 * - default: Main content area for node-specific UI
 * - context: Node type label/icon area
 * - hover: Tooltip content shown on hover
 * - settings-menu: Webapp-specific menu (edit, duplicate, delete)
 *
 * This component handles all the common node behaviors so individual
 * node types can focus on their specific functionality.
 */

// Inject preview modes from parent context
const preview = inject('preview', false)
const templatePreview = inject('templatePreview', false)
const canAddRef = inject<Ref<boolean>>('canAdd', ref(false))
const canAdd = computed(() => unref(canAddRef))
const adapter = inject('adapter', {})

// Inject data for validation
const offers = inject('offers', ref([]))
const questions = inject('questions', ref([]))
const segmentGroups = inject('segmentGroups', ref([])) 

// Inject clone mode state
const cloneMode = inject<any>('cloneMode', ref(null))

// Inject heat map related data
const showHeatMap = inject<any>('showHeatMap', ref(false))
const maxVisitorCount = inject<any>('maxVisitorCount', ref(0))
const getRawNodeMetrics = inject<(node: any) => any>('getRawNodeMetrics', () => null)

// Check if enhanced reporting is enabled via adapter's global settings or injected settings
const settings = inject<any>('settings', ref({}))
const useEnhancedReporting = computed(() => {
  // First check adapter's globalSettings if available
  if (adapter?.globalSettings?.value?.enhancedFlowReporting !== undefined) {
    return adapter.globalSettings.value.enhancedFlowReporting === true
  }
  // Fall back to injected settings
  return settings.value?.enhancedFlowReporting === true
})

interface MenuItem {
  label: string
  icon?: any
  command?: () => void
  separator?: boolean
}

const props = defineProps<{
  classes?: string
  icon?: Component
  node: any
  metrics?: Array<{ label: string; value: string | number }>
  miniMetrics?: boolean
  hideMenu?: boolean
  hideDuplicate?: boolean
  dropMaxSize?: boolean // Whether to remove max-width constraint
  menuItems?: MenuItem[]
  draggable?: boolean // Whether node can be dragged
  linkId?: string
  deleteLabel?: string
  context?: string
  variantId?: string // For A/B test variants
  disableDrag?: boolean
  needsSetup?: boolean // Override needs setup check
  setupLabel?: string // Custom label for setup badge (default: "Needs Setup")
}>()

const emit = defineEmits<{
  click: []
  'node-action': [action: string, data?: any]
  'delete-node': []
}>()

// Element ref for positioning calculations
const el = ref<HTMLElement | null>(null)
const injectedTeleportTarget = inject<Ref<HTMLElement | null>>('teleportTarget', ref(null))
const tooltipTeleportTarget = computed(() => injectedTeleportTarget.value || 'body')

// Icon components for settings menu
const nodeProps = inject('nodeProps', ref({}))
const settingsIcon = computed(
  () => nodeProps.value.icons?.EllipsisVerticalIcon || EllipsisVerticalIcon
)
const trashIcon = computed(() => nodeProps.value.icons?.TrashIcon || TrashIcon)
const duplicateIcon = computed(() => nodeProps.value.icons?.DocumentDuplicateIcon || DocumentDuplicateIcon)

const resolvedIcon = computed(() => resolveFlowIcon(props.icon))

const visitorCount = computed(() => {
  if (props.metrics) {
    return props.metrics.find((o) => o.label === 'Visitors')?.value
  }
})

// Heat map style calculation
const heatMapStyle = computed(() => {
  if (!showHeatMap.value || preview || templatePreview) {
    return {}
  }
  
  // Get raw metrics for this node
  const nodeMetrics = getRawNodeMetrics(props.node)
  if (!nodeMetrics || !nodeMetrics.visitorCount || !maxVisitorCount.value) {
    return {}
  }
  
  const count = nodeMetrics.visitorCount
  const max = maxVisitorCount.value
  
  // Calculate color intensity based on visitor count
  // Using a logarithmic scale for better distribution
  const normalized = Math.log(count + 1) / Math.log(max + 1)
  
  // Heat map colors from cold (blue) to hot (red)
  const hue = 240 - (normalized * 240) // From blue (240) to red (0)
  const saturation = 50 + (normalized * 50) // 50% to 100%
  const lightness = 90 - (normalized * 40) // 90% to 50%
  
  return {
    backgroundColor: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
    borderColor: `hsl(${hue}, ${saturation}%, ${lightness - 20}%)`,
    transition: 'background-color 0.3s ease, border-color 0.3s ease'
  }
})

// Tooltip state management
const showTooltip = ref(false)
const tooltipX = ref(0)
const tooltipY = ref(0)
const tooltipEl = ref<HTMLElement | null>(null)
const tooltipMaxHeight = ref('calc(100vh - 16px)')

// Dark mode detection
const isDark = computed(() => {
  if (typeof document !== 'undefined') {
    return (
      document.documentElement.classList.contains('dark') ||
      document.body.classList.contains('dark')
    )
  }
  return false
})

/**
 * Check if node needs setup
 * A node needs setup if it's missing required configuration.
 * This is determined by the isNodeSetUp utility function which
 * checks node-specific requirements (e.g., offers need content).
 * Can be overridden via the needsSetup prop for custom conditions.
 */
const computedNeedsSetup = computed(() => {
  // If needsSetup prop is explicitly provided, use it
  if (props.needsSetup !== undefined) {
    return props.needsSetup
  }

  // Otherwise, use the default node setup check
  const context = {
    offers: offers.value,
    questions: questions.value,
    segmentGroups: segmentGroups.value
  }
  return !isNodeSetUp(props.node, context)
})

/**
 * Check if node is clickable
 * Nodes are clickable when not in preview mode
 */
const isClickable = computed(() => {
  return !preview && !templatePreview
})

/**
 * Check if node can be dragged
 * Nodes can be dragged when not in preview/read-only mode and draggable prop is true (default)
 */
const canDrag = computed(() => {
  return !preview && !templatePreview && !props.disableDrag && canAdd.value
})

/**
 * Link component and props
 * If the adapter provides a getLinkProps function and the node is clickable,
 * use the link component (e.g., router-link). Otherwise, use a div.
 * Only use router-link if getLinkProps returns a valid route.
 */
const linkProps = computed(() => {
  if (isClickable.value && adapter?.getLinkProps) {
    return adapter.getLinkProps(props.node, props.linkId, props.variantId)
  }
  return {}
})

const linkComponent = computed(() => {
  // Only use router-link if we have valid link props with a 'to' property
  if (
    isClickable.value &&
    adapter?.getLinkProps &&
    adapter?.linkComponent &&
    linkProps.value?.to
  ) {
    return adapter.linkComponent
  }
  return 'div'
})

// Track menu state
const isMenuOpen = ref(false)
const menuJustClosed = ref(false)

const handleNodeClick = (event: MouseEvent) => {
  // Don't handle clicks on interactive elements
  const target = event.target as HTMLElement
  if (target.closest('button, [role="button"], .settings-menu')) {
    return
  }

  // Don't handle clicks if menu was just closed
  if (menuJustClosed.value) {
    return
  }

  // Only emit click if we're not using a link component
  // (link component handles navigation itself)
  if (isClickable.value && linkComponent.value === 'div') {
    emit('click')
  }
}

const handleMenuClosed = () => {
  isMenuOpen.value = false
  menuJustClosed.value = true

  // Reset flag after a delay
  setTimeout(() => {
    menuJustClosed.value = false
  }, 200)
}

/**
 * Enter clone mode with this node as the source.
 * All dropzones will pulse gold, and clicking one will clone
 * this node (including descendants) into that position.
 */
const handleCloneNode = () => {
  if (cloneMode) {
    cloneMode.value = { active: true, sourceNode: props.node }
  }
}

/**
 * Handle drag start
 * Store the node data and notify the adapter
 * If Alt/Option key is held, duplicate the node instead of moving it
 */
const handleDragStart = (event: DragEvent) => {
  if (!canDrag.value) return

  // Prevent event from bubbling to parent elements
  event.stopPropagation()

  // Check if Alt/Option key is held for duplication
  const isDuplicating = event.altKey

  if (isDuplicating) {
    // Deep clone the node with fresh UUIDs - this is a duplicate operation
    const clonedNode = deepCloneNode(props.node)
    event.dataTransfer?.setData('node', JSON.stringify({ node: clonedNode }))
    event.dataTransfer!.effectAllowed = 'copy'

    // isExisting: false tells drop zones to INSERT the node (not move)
    if (adapter.setDragging) {
      adapter.setDragging({ node: clonedNode, isExisting: false })
    }
  } else {
    // Normal move operation
    event.dataTransfer?.setData('node', JSON.stringify({ node: props.node }))
    event.dataTransfer!.effectAllowed = 'copyMove'

    // Notify adapter that dragging has started
    if (adapter.setDragging) {
      adapter.setDragging({ node: props.node, isExisting: true })
    }
  }
}

/**
 * Handle drag end
 * Clear dragging state
 */
const handleDragEnd = (event: DragEvent) => {
  // Prevent event from bubbling
  event.stopPropagation()

  // Notify adapter that dragging has ended
  if (adapter.setDragging) {
    adapter.setDragging(null)
  }
}

/**
 * Smart tooltip positioning
 *
 * Calculates optimal tooltip position to:
 * - Stay within viewport bounds
 * - Appear to the right of the node by default
 * - Flip to left if insufficient space on right
 * - Adjust vertical position if needed
 *
 * This ensures tooltips are always fully visible regardless
 * of where the node is positioned on screen.
 */
const TOOLTIP_MARGIN = 8
const TOOLTIP_GAP = 12

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max)
}

const positionTooltip = (
  nodeRect: DOMRect,
  tooltipRect: Pick<DOMRect, 'width' | 'height'> = { width: 300, height: 100 }
) => {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const maxTooltipWidth = Math.max(0, viewportWidth - TOOLTIP_MARGIN * 2)
  const maxTooltipHeight = Math.max(120, viewportHeight - TOOLTIP_MARGIN * 2)
  const tooltipWidth = Math.min(tooltipRect.width || 300, maxTooltipWidth)
  const tooltipHeight = Math.min(tooltipRect.height || 100, maxTooltipHeight)

  const rightX = nodeRect.right + TOOLTIP_GAP
  const leftX = nodeRect.left - tooltipWidth - TOOLTIP_GAP
  const preferredX =
    rightX + tooltipWidth <= viewportWidth - TOOLTIP_MARGIN
      ? rightX
      : leftX

  tooltipX.value = clamp(
    preferredX,
    TOOLTIP_MARGIN,
    viewportWidth - tooltipWidth - TOOLTIP_MARGIN
  )
  tooltipY.value = clamp(
    nodeRect.top + nodeRect.height / 2 - tooltipHeight / 2,
    TOOLTIP_MARGIN,
    viewportHeight - tooltipHeight - TOOLTIP_MARGIN
  )
  tooltipMaxHeight.value = `${maxTooltipHeight}px`
}

let tooltipResizeObserver: ResizeObserver | null = null

const disconnectTooltipResizeObserver = () => {
  tooltipResizeObserver?.disconnect()
  tooltipResizeObserver = null
}

const repositionTooltip = () => {
  if (!el.value || !tooltipEl.value) return

  positionTooltip(
    el.value.getBoundingClientRect(),
    tooltipEl.value.getBoundingClientRect()
  )
}

const observeTooltipSize = () => {
  disconnectTooltipResizeObserver()

  if (!tooltipEl.value || typeof ResizeObserver === 'undefined') return

  tooltipResizeObserver = new ResizeObserver(() => {
    if (showTooltip.value) repositionTooltip()
  })
  tooltipResizeObserver.observe(tooltipEl.value)
}

const showTooltipForNode = () => {
  if (!el.value) return

  const rect = el.value.getBoundingClientRect()

  positionTooltip(rect)
  showTooltip.value = true

  nextTick(() => {
    if (!showTooltip.value || !tooltipEl.value) return
    observeTooltipSize()
    repositionTooltip()
  })
}

const hideTooltip = () => {
  showTooltip.value = false
  disconnectTooltipResizeObserver()
}

onBeforeUnmount(disconnectTooltipResizeObserver)

// Expose element ref for parent components
defineExpose({
  el
})
</script>

